import {
  AnalyticsQuery, encodeGeneralInspectionQuery,
  encodeInspectionQuery,
  encodeIssueQuery,
  encodeQuestionQuery, GeneralInspectionQuery,
  InspectionQuery,
  IssueQuery,
  QuestionQuery,
  unsetSort,
} from '@/services/api/resources/analytics/query';
import { pushRoute } from '@/ui/helpers/uiHelper';
import { AnalyticsGroupBy, InspectionGroupBy, QuestionGroupBy } from '@/services/api/resources/analytics/types';
import { Status } from '@/entities/todo';
import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
import { FormsTabNames, AnalyticsTabNames, queryTabName } from '@/ui/components/navigation/constants';

export const redirectOverdueInspectionsExplain = (query: InspectionQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.OVERDUE_INSPECTIONS);
  return pushRoute({
    name: 'analyticsOverdueInspectionsExplain',
    query: encodeInspectionQuery(query),
  });
};
export const redirectOverdueInspections = (query: InspectionQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.OVERDUE_INSPECTIONS);
  return pushRoute({
    name: 'analytics',
    query: { [queryTabName]: AnalyticsTabNames.OVERDUE_INSPECTIONS, ...encodeInspectionQuery(query) },
  });
};
export const redirectNegativeResponsesExplain = (query: QuestionQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.NEGATIVE_RESPONSES);
  return pushRoute({
    name: 'analyticsNegativeResponsesExplain',
    query: encodeQuestionQuery(query),
  });
};
export const redirectNegativeResponses = (query: QuestionQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.NEGATIVE_RESPONSES);
  return pushRoute({
    name: 'forms',
    query: { [queryTabName]: FormsTabNames.NEGATIVE_RESPONSES, ...encodeQuestionQuery(query) },
  });
};
export const redirectScoresExplain = (query: QuestionQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.AVERAGE_SCORE);
  return pushRoute({
    name: 'analyticsScoresExplain',
    query: encodeQuestionQuery(query),
  });
};
export const redirectScores = (query: QuestionQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.AVERAGE_SCORE);
  return pushRoute({
    name: 'analytics',
    query: { [queryTabName]: AnalyticsTabNames.SCORES, ...encodeQuestionQuery(query) },
  });
};
export const redirectIssuesExplain = (query: IssueQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.ISSUES);
  return pushRoute({
    name: 'analyticsIssuesExplain',
    query: encodeIssueQuery(query),
  });
};
export const redirectIssues = (query: IssueQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.ISSUES);
  return pushRoute({
    name: 'analytics',
    query: { [queryTabName]: AnalyticsTabNames.ISSUES, ...encodeIssueQuery(query) },
  });
};
export const redirectSubmittedForms = (query: GeneralInspectionQuery) => {
  trackClick(CLICK_EVENTS.TILES.NUMBER_OF.INSPECTIONS);
  return pushRoute({
    name: 'forms',
    query: { [queryTabName]: FormsTabNames.SUBMITTED, ...encodeGeneralInspectionQuery(query) },
  });
};

const prepareTableRedirectQuery = (query: AnalyticsQuery, row: { group: { id: number } }, groupBy: AnalyticsGroupBy) =>
  (unsetSort({
    ...query,
    ...groupBy === 'Question' && { question: [row.group.id.toString()] },
    ...groupBy === 'Inspector' && {
      user: [Number(row.group.id)],
      inspector: [Number(row.group.id)],
    },
    ...groupBy === 'EntityItem' && { entityItem: [Number(row.group.id)] },
    ...groupBy === 'Group' && { group: [Number(row.group.id)] },
    ...groupBy === 'Category' && { category: [Number(row.group.id)] },
    ...groupBy === 'Checklist' && { checklist: [Number(row.group.id)] },
    ...groupBy === 'Section' && { section: [row.group.id.toString()] },
  }) as AnalyticsQuery);

export const handleNegativeResponseClick = (
  query: QuestionQuery, row: { group: { id: number } }, groupBy: QuestionGroupBy,
) => pushRoute({
  name: 'analyticsNegativeResponsesExplain',
  query: encodeQuestionQuery((prepareTableRedirectQuery(query, row, groupBy) as QuestionQuery)),
});

export const handleScoreClick = (
  query: QuestionQuery, row: { group: { id: number } }, groupBy: QuestionGroupBy,
) => pushRoute({
  name: 'analyticsScoresExplain',
  query: encodeQuestionQuery((prepareTableRedirectQuery(query, row, groupBy) as QuestionQuery)),
});

export const handleOverdueInspectionClick = (
  query: InspectionQuery, row: { group: { id: number } }, groupBy: InspectionGroupBy,
) => pushRoute({
  name: 'analyticsOverdueInspectionsExplain',
  query: encodeInspectionQuery((prepareTableRedirectQuery(query, row, groupBy) as InspectionQuery)),
});

export const handleIssueClick = (query: IssueQuery, meta: { status: Status }) =>
  pushRoute({
    name: 'analyticsIssuesExplain',
    query: encodeIssueQuery(({
      ...query,
      status: [Number(meta.status)],
    } as IssueQuery)),
  });
